import React, { useEffect, useRef, useState } from 'react';
import { Tattoo } from '../types';

interface GalleryProps {
  tattoos: Tattoo[];
  activeCategory: string | null;
  onTattooSelect: (tattoo: Tattoo) => void | Promise<void>;
  hideText?: boolean;
  hasPremiumAccess: boolean;
  onRequirePremium: () => void;
  selectedTattooId?: number | null;
}

const Gallery: React.FC<GalleryProps> = ({
  tattoos,
  activeCategory,
  onTattooSelect,
  hideText = false,
  hasPremiumAccess,
  onRequirePremium,
  selectedTattooId
}) => {
  const [selectedTattoo, setSelectedTattoo] = useState<Tattoo | null>(null);
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  const handleTattooClick = async (tattoo: Tattoo) => {
    if (tattoo.isPremium && !hasPremiumAccess) {
      onRequirePremium();
      return;
    }
    setSelectedTattoo(tattoo);
    await onTattooSelect(tattoo);
  };

  const handleImageLoad = (tattooId: number) => {
    setLoadedImages(prev => new Set(prev).add(tattooId));
  };

  useEffect(() => {
    if (selectedTattooId === undefined) {
      return;
    }

    if (selectedTattooId === null) {
      setSelectedTattoo(null);
      return;
    }

    const tattoo = tattoos.find(t => t.id === selectedTattooId) || null;
    setSelectedTattoo(tattoo);
  }, [selectedTattooId, tattoos]);

  const filteredTattoos = activeCategory
    ? tattoos.filter(tattoo => tattoo.categoryId === activeCategory)
    : tattoos;

  useEffect(() => {
    if (selectedTattooId == null) {
      return;
    }
    const selectedCard = cardRefs.current[selectedTattooId];
    if (!selectedCard) {
      return;
    }
    selectedCard.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: 'smooth'
    });
  }, [selectedTattooId]);

  // Track window resize so banner order updates without reload
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  
  return (
    <div className="samples__wrapper"> 
    <div className="samples__body">
      {filteredTattoos.map((tattoo, index) => {
        const isHome = typeof window !== 'undefined' && window.location.pathname === '/';
        
        return (
          <React.Fragment key={tattoo.id}>
            <div 
              className={`mini-card mini-card--${tattoo.cssClass || 'dark'} ${selectedTattoo?.id === tattoo.id ? 'mini-card--selected' : ''} ${loadedImages.has(tattoo.id) ? 'mini-card--loaded' : ''} ${tattoo.isPremium && !hasPremiumAccess ? 'mini-card--locked' : ''}`}
              data-tattoo-id={tattoo.id}
              data-tattoo-category={tattoo.categoryId}
              data-tattoo-image={tattoo.image}
              data-analytics-source="gallery"
              onClick={() => handleTattooClick(tattoo)}
              ref={(el) => {
                if (el) {
                  cardRefs.current[tattoo.id] = el;
                } else {
                  delete cardRefs.current[tattoo.id];
                }
              }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/content/images/tattoos/${tattoo.image}`} 
                alt={`${tattoo.categoryId} tattoo`} 
                className="mini-card__img"
                loading="lazy"
                width={64}
                height={64}
                onLoad={() => handleImageLoad(tattoo.id)}
              />
              {!hideText && (
                <div className="mini-card__text">
                  {selectedTattoo?.id === tattoo.id ? 'Selected' : 'Try this'}
                </div>
              )}

              {tattoo.isPremium && !hasPremiumAccess && (
                <div className="mini-card__premium">
                  <img src="/img/lock.png" alt="lock" />
                  <div className="mini-card__text">Premium</div>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  </div>
  );
};

export default Gallery;
